/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ChangeEvent, FunctionComponent, JSX } from "react";
import { useCallback, useEffect, useRef } from "react";

import type { AppVersionOverlayProps } from "./AppVersionOverlayProps";

import "./AppVersionOverlay.scss";

const TITLE_ID: string = "app-version-overlay-title";
const INPUT_ID: string = "app-version-overlay-input";
const MOCK_INPUT_ID: string = "app-version-overlay-mock-input";
const ERROR_ID: string = "app-version-overlay-error";
const MOCK_ERROR_ID: string = "app-version-overlay-mock-error";
const ACTIVE_ID: string = "app-version-overlay-active";
const ACTIVE_MOCK_ID: string = "app-version-overlay-active-mock";

/** Focus indices, mirroring `AppVersionOverrideFocus` in the slice. */
const FOCUS_INPUT: number = 0;
const FOCUS_APPLY: number = 1;
const FOCUS_RESET: number = 2;
const FOCUS_MOCK_INPUT: number = 3;
const FOCUS_MOCK_APPLY: number = 4;
const FOCUS_MOCK_RESET: number = 5;

/** Class applied to whichever button currently holds D-pad focus. */
const BUTTON_CLASS: string = "app-version-overlay__button";
const BUTTON_FOCUSED_CLASS: string = "app-version-overlay__button--focused";

function buttonClass(isFocused: boolean): string {
  return BUTTON_CLASS + (isFocused ? " " + BUTTON_FOCUSED_CLASS : "");
}

function fieldClass(isFocused: boolean): string {
  return (
    "app-version-overlay__field" +
    (isFocused ? " app-version-overlay__field--focused" : "")
  );
}

/**
 * AppVersionOverlay — DEBUG-ONLY override UI (web-app version + mock host).
 *
 * Opened by the hidden `54321` code and never reachable in normal use; see
 * `useAppVersionOverrideCode`. Purely presentational: all state lives in
 * `viewModel/appVersionOverride` and is wired up by
 * `AppVersionOverlayContainer`.
 *
 * Two independent overrides, laid out identically: a field, its own
 * Apply/Reset pair, its own error line. They are separate because they answer
 * different questions — which *build* runs, and which *backend* it talks to —
 * and are usually changed one at a time, so a shared Apply would make every
 * mock-host change also rewrite the version (and vice versa).
 *
 * Both fields are real `<input>`s that take HTML focus when D-pad focus is on
 * them, the same arrangement `SignIn`'s `InputField` uses — that is what
 * raises the TV's on-screen keyboard and lets a non-numeric value such as
 * `v1.0.561` or `192.168.0.2:4500|505c38b651cb` be typed at all. That matters
 * most for the mock host: on a TV there is no address bar, so this overlay is
 * the only way to point a packaged `.wgt`/`.ipk` build at a mock server, where
 * in a browser `?mockHost=` would also do.
 *
 * `NavigationEngine` keeps routing the arrow keys while a text field has
 * focus, so D-pad can still move down through the fields to the buttons; OK
 * does not reach the modal controller from inside a field, which is why Apply
 * is a button the developer moves to rather than an Enter handler.
 */
const AppVersionOverlay: FunctionComponent<AppVersionOverlayProps> = (
  props: AppVersionOverlayProps
): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09) for logging context
  const TAG: string = "AppVersionOverlay";

  const {
    versionInput,
    mockHostInput,
    activeVersion,
    activeMockHost,
    versionErrorMessage,
    mockHostErrorMessage,
    focusedIndex,
    onVersionInputChange,
    onMockHostInputChange,
    onFocusControl,
    onApplyVersion,
    onResetVersion,
    onApplyMockHost,
    onResetMockHost
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const mockInputRef = useRef<HTMLInputElement>(null);

  const hasVersionError: boolean = versionErrorMessage.length > 0;
  const hasMockHostError: boolean = mockHostErrorMessage.length > 0;
  const hasActiveVersion: boolean = activeVersion.length > 0;
  const hasActiveMockHost: boolean = activeMockHost.length > 0;

  useEffect((): void => {
    if (inputRef.current !== null) {
      if (focusedIndex === FOCUS_INPUT) {
        inputRef.current.focus();
      } else {
        inputRef.current.blur();
      }
    }
    if (mockInputRef.current !== null) {
      if (focusedIndex === FOCUS_MOCK_INPUT) {
        mockInputRef.current.focus();
      } else {
        mockInputRef.current.blur();
      }
    }
  }, [focusedIndex]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      onVersionInputChange(event.target.value);
    },
    [onVersionInputChange]
  );

  const handleMockChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      onMockHostInputChange(event.target.value);
    },
    [onMockHostInputChange]
  );

  const handleInputFocus = useCallback((): void => {
    onFocusControl(FOCUS_INPUT);
  }, [onFocusControl]);

  const handleApplyFocus = useCallback((): void => {
    onFocusControl(FOCUS_APPLY);
  }, [onFocusControl]);

  const handleResetFocus = useCallback((): void => {
    onFocusControl(FOCUS_RESET);
  }, [onFocusControl]);

  const handleMockInputFocus = useCallback((): void => {
    onFocusControl(FOCUS_MOCK_INPUT);
  }, [onFocusControl]);

  const handleMockApplyFocus = useCallback((): void => {
    onFocusControl(FOCUS_MOCK_APPLY);
  }, [onFocusControl]);

  const handleMockResetFocus = useCallback((): void => {
    onFocusControl(FOCUS_MOCK_RESET);
  }, [onFocusControl]);

  // Each field describes itself when healthy and its own error when not, so a
  // screen reader on the mock field never announces the version's status.
  const describedBy: string = hasVersionError ? ERROR_ID : ACTIVE_ID;
  const mockDescribedBy: string = hasMockHostError
    ? MOCK_ERROR_ID
    : ACTIVE_MOCK_ID;

  return (
    <div className="app-version-overlay">
      <div className="app-version-overlay__scrim" aria-hidden="true" />
      <div
        className="app-version-overlay__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
      >
        <h2 id={TITLE_ID} className="app-version-overlay__title">
          Debug Overrides
        </h2>

        <div className="app-version-overlay__section">
          <label className="app-version-overlay__label" htmlFor={INPUT_ID}>
            Web App Version
          </label>

          <p id={ACTIVE_ID} className="app-version-overlay__active">
            {hasActiveVersion
              ? "Version: " + activeVersion
              : "Version: default (latest)"}
          </p>

          <div className={fieldClass(focusedIndex === FOCUS_INPUT)}>
            <input
              ref={inputRef}
              id={INPUT_ID}
              className="app-version-overlay__input"
              type="text"
              value={versionInput}
              placeholder="v1.0.561"
              aria-label="Web app version"
              aria-invalid={hasVersionError}
              aria-describedby={describedBy}
              onChange={handleChange}
              onFocus={handleInputFocus}
            />
          </div>

          {hasVersionError && (
            <p
              id={ERROR_ID}
              className="app-version-overlay__error"
              role="alert"
            >
              {versionErrorMessage}
            </p>
          )}

          <div className="app-version-overlay__actions">
            <button
              type="button"
              className={buttonClass(focusedIndex === FOCUS_APPLY)}
              tabIndex={0}
              onClick={onApplyVersion}
              onMouseEnter={handleApplyFocus}
              onFocus={handleApplyFocus}
            >
              Apply Version
            </button>
            <button
              type="button"
              className={buttonClass(focusedIndex === FOCUS_RESET)}
              tabIndex={0}
              onClick={onResetVersion}
              onMouseEnter={handleResetFocus}
              onFocus={handleResetFocus}
            >
              Reset Version
            </button>
          </div>
        </div>

        <div className="app-version-overlay__section">
          <label className="app-version-overlay__label" htmlFor={MOCK_INPUT_ID}>
            Mock Host
          </label>

          <p id={ACTIVE_MOCK_ID} className="app-version-overlay__active">
            {hasActiveMockHost
              ? "Mock: " + activeMockHost
              : "Mock: off (real backend)"}
          </p>

          <div className={fieldClass(focusedIndex === FOCUS_MOCK_INPUT)}>
            <input
              ref={mockInputRef}
              id={MOCK_INPUT_ID}
              className="app-version-overlay__input"
              type="text"
              value={mockHostInput}
              placeholder="192.168.0.2:4500|505c38b651cb"
              aria-label="Mock host and token"
              aria-invalid={hasMockHostError}
              aria-describedby={mockDescribedBy}
              onChange={handleMockChange}
              onFocus={handleMockInputFocus}
            />
          </div>

          {hasMockHostError && (
            <p
              id={MOCK_ERROR_ID}
              className="app-version-overlay__error"
              role="alert"
            >
              {mockHostErrorMessage}
            </p>
          )}

          <div className="app-version-overlay__actions">
            <button
              type="button"
              className={buttonClass(focusedIndex === FOCUS_MOCK_APPLY)}
              tabIndex={0}
              onClick={onApplyMockHost}
              onMouseEnter={handleMockApplyFocus}
              onFocus={handleMockApplyFocus}
            >
              Apply Mock Host
            </button>
            <button
              type="button"
              className={buttonClass(focusedIndex === FOCUS_MOCK_RESET)}
              tabIndex={0}
              onClick={onResetMockHost}
              onMouseEnter={handleMockResetFocus}
              onFocus={handleMockResetFocus}
            >
              Reset Mock Host
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppVersionOverlay;
